import axios, { AxiosInstance, AxiosResponse } from "axios";
import jwtDecode from "jwt-decode";
import "../../ui/styles/sessionexpired.css";
import swal from "sweetalert";
import { useLocation } from "react-router";
import { useEffect } from "react";
import {
  clearClientStorage,
  endClientSession,
  getStoredUser,
  getToken,
  isTokenExpired,
  setSession,
} from "./session";

interface Routes {
  auth: string;
  pins: string;
  waitlist: string;
  accounts: string;
  platforms: string;
  contacts: string;
  responses: string;
  domains: string;
  onboarding: string;
  stripe: string;
}
interface DecodedToken {
  id: string;
  exp: number;
}
// export let token = localStorage.getItem("jwtToken");

export let instance: AxiosInstance = axios.create({
  withCredentials: true,
  timeout: 300000,
  baseURL: `${process.env.REACT_APP_API}`,
  // headers: { Authorization: token ? token : "token" },
});
const isLogoutRequest = (url?: string) => !!url && url.includes("/bpi/logout");

instance.interceptors.request.use(
  function (config) {
    const token = getToken();
    if (token && isTokenExpired(token) && !isLogoutRequest(config?.url)) {
      endClientSession("/login");
      return Promise.reject(new axios.Cancel("Session expired"));
    }
    if (token && config && config.headers)
      config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const token = getToken();
    if (
      token &&
      !isLogoutRequest(error.config?.url) &&
      (error.response?.status === 403 || error.response?.status === 401)
    ) {
      clearClientStorage();
      swal("Session Expired", "Please sign in again").then(() => {
        window.location.assign("/login");
      });
    }
    if (error.response?.status === 500) {
      // window.location.assign("/500");
    }
    return Promise.reject(error);
  },
);

const routes: Routes = {
  auth: "auth",
  pins: "pins",
  waitlist: "waitlist",
  accounts: "resources/accounts",
  platforms: "resources/platforms",
  contacts: "resources/contacts",
  responses: "resources/responses",
  domains: "resources/domains",
  onboarding: "onboarding",
  stripe: "stripe",
};
export const fetcher = (url: string) =>
  instance.get(url).then((res) => res.data);
export const post = async (
  route: keyof Routes,
  path?: string,
  payload?: any,
  blockExtend = false,
): Promise<any> => {
  const data = await instance.post(`/${routes[route]}/${path}`, payload);
  if (!blockExtend) {
    // await extend();
    // await revalidateLiveQueries();
  }
  return data;
};
export const update = async (
  route: keyof Routes,
  path: string,
  payload: any,
) => {
  const data = await instance.put(`/${routes[route]}/${path}`, payload);
  // await revalidateLiveQueries();
  //   await extend();
  return data;
};
export const del = async (route: keyof Routes, path: any) => {
  const data = await instance.delete(`/${routes[route]}/${path}`);
  // await revalidateLiveQueries();
  //   await extend();
  return data;
};

/**
 * Asks the API to revoke the current token. Never throws: callers must clear
 * local state whether or not the server could be reached.
 * Uses POST /auth/bpi/logout and falls back to the legacy
 * GET /auth/bpi/logout/:account while the backend still only exposes that.
 */
export async function revokeSession(accountId?: string): Promise<void> {
  if (!getToken()) return;
  try {
    await instance.post(`/${routes.auth}/bpi/logout`);
  } catch (error: any) {
    if (error?.response?.status === 404 && accountId) {
      await instance
        .get(`/${routes.auth}/bpi/logout/${accountId}`)
        .catch(() => undefined);
    }
  }
}

export async function extend() {
  let token = getToken();
  if (!token) return;

  const { id, exp } = jwtDecode(token) as DecodedToken;
  const refreshCutOff = Date.now() + 60000 * 28;

  if (refreshCutOff > exp * 1000) {
    try {
      const request = await post("auth", `extend/${id}`, {}, true);
      setSession(request.data, getStoredUser());
    } catch (error) {
      console.log(error);
    }
  }
}